/**
 * Tab notification utilities for showing unread message counts
 * in the browser tab title and favicon badge.
 *
 * Features:
 * - Tab title shows unread count: "(3) BandMate"
 * - Favicon shows red badge with count
 * - Title flashes to catch attention when tab not focused
 */

const BASE_TITLE = 'BandMate';
const originalFavicon = '/favicon.svg';

// Module-level state
let unreadCount = 0;
let flashInterval: ReturnType<typeof setInterval> | null = null;
let isFlashOn = true;

/**
 * Generate a favicon SVG with an optional notification count badge
 */
function createFaviconWithBadge(count: number): string {
  if (count === 0) return originalFavicon;

  const displayCount = count > 9 ? '9+' : String(count);
  const fontSize = count > 9 ? 22 : 26;

  return `data:image/svg+xml,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
  <circle cx="50" cy="50" r="45" fill="#22c55e"/>
  <text x="50" y="65" text-anchor="middle" font-family="Inter, sans-serif" font-weight="bold" font-size="40" fill="#0f172a">B</text>
  <circle cx="78" cy="22" r="20" fill="#ef4444"/>
  <text x="78" y="30" text-anchor="middle" font-family="Inter, sans-serif" font-weight="bold" font-size="${fontSize}" fill="white">${displayCount}</text>
</svg>
  `)}`;
}

/**
 * Update the favicon element
 */
function setFavicon(href: string): void {
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
}

/**
 * Start flashing the title to catch attention
 */
function startFlashing(): void {
  if (flashInterval) return; // Already flashing

  flashInterval = setInterval(() => {
    isFlashOn = !isFlashOn;
    if (isFlashOn) {
      document.title = `(${unreadCount}) ${BASE_TITLE}`;
    } else {
      document.title = `💬 New Message!`;
    }
  }, 1000);
}

/**
 * Stop flashing the title
 */
function stopFlashing(): void {
  if (flashInterval) {
    clearInterval(flashInterval);
    flashInterval = null;
    isFlashOn = true;
  }
}

/**
 * Update tab title and favicon to show unread count
 */
export function updateTabIndicators(count: number): void {
  unreadCount = count;

  if (count > 0) {
    document.title = `(${count}) ${BASE_TITLE}`;
    setFavicon(createFaviconWithBadge(count));
    
    // Start flashing if tab not focused
    if (!document.hasFocus()) {
      startFlashing();
    }
  } else {
    document.title = BASE_TITLE;
    setFavicon(originalFavicon);
    stopFlashing();
  }
}

/**
 * Increment unread count when receiving a message
 */
export function incrementUnread(): void {
  updateTabIndicators(unreadCount + 1);
}

/**
 * Clear unread indicators (call when user focuses tab or reads messages)
 */
export function clearUnread(): void {
  updateTabIndicators(0);
}

/**
 * Get the current unread count
 */
export function getUnreadCount(): number {
  return unreadCount;
}

/**
 * Check if the tab currently has focus
 */
export function isTabFocused(): boolean {
  return document.hasFocus();
}

/**
 * Set up a focus listener to automatically clear indicators when tab is focused.
 * Returns a cleanup function.
 */
export function setupFocusListener(): () => void {
  const handleFocus = () => {
    stopFlashing();
    clearUnread();
  };

  window.addEventListener('focus', handleFocus);
  return () => {
    window.removeEventListener('focus', handleFocus);
    stopFlashing();
  };
}
